'use client';

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';

import { cn } from '@/utils/cn-tw-merge';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return <RadioGroupPrimitive.Root className={cn('grid gap-2', className)} {...props} ref={ref} />;
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
    circleSize?: string;
  }
>(({ className, circleSize, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'rounded-full border border-gray-400 text-green-500 ring-offset-green-500',
        'data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500',
        'disabled:cursor-not-allowed disabled:opacity-50',
        resolveCircleSize(circleSize),
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center bg-green-500 h-full w-full rounded-full">
        <Circle className="h-3 w-3 fill-green-500 border-white bg-white border rounded-full text-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

function resolveCircleSize(size?: string) {
  if (!size) {
    return 'h-4 w-4';
  }
  switch (size) {
    case 'sm':
      return 'h-3 w-3';
    case 'md':
      return 'h-4 w-4';
    case 'lg':
      return 'h-5 w-5';
    case 'xl':
      return 'h-6 w-6';
    default:
      return 'h-4 w-4';
  }
}

export { RadioGroup, RadioGroupItem };
